import type { ComponentPropsWithRef, ReactNode } from "react";
import { forwardRef, useMemo } from "react";
import classNames from "classnames";
import { uniqueId } from "lodash";

export interface WithDescriptionProps extends ComponentPropsWithRef<"input"> {
  className?: string;
  description?: ReactNode;
  descriptionClassName?: string;
  id?: string;
  innerBlock?: ReactNode;
  label: ReactNode;
  labelClassName?: string;
  labelWrapperClassName?: string;
  wrapperClassName?: string;
}

export const radioButtonClassNames =
  "bg-primary hover:bg-tertiary focus:ring-secondary focus:bg-primary text-dark border-primary h-4 w-4 cursor-pointer";
export const WithDescription = forwardRef<
  HTMLInputElement,
  WithDescriptionProps
>(
  (
    {
      id,
      label,
      className,
      wrapperClassName,
      labelClassName,
      labelWrapperClassName,
      description,
      descriptionClassName,
      innerBlock,
      ...rest
    },
    ref,
  ) => {
    const inputId = useMemo(() => {
      if (id) return id;
      return uniqueId();
    }, [id]);

    return (
      <div
        className={classNames("relative flex items-start", wrapperClassName)}
      >
        <div className="flex h-5 items-center">
          <input
            id={inputId}
            aria-describedby={`${inputId}-description`}
            type="radio"
            className={classNames(radioButtonClassNames, className)}
            {...rest}
            ref={ref}
          />
        </div>
        <div className={classNames("ml-3 text-sm", labelWrapperClassName)}>
          <label
            htmlFor={inputId}
            className={classNames("text-darkest font-medium", labelClassName)}
          >
            {label}
          </label>
          <p
            id={`${inputId}-description`}
            className={classNames("text-dark", descriptionClassName)}
          >
            {description}
          </p>
          {innerBlock}
        </div>
      </div>
    );
  },
);

WithDescription.displayName = "WithDescription";
